import React, { useState, useEffect, useRef, useCallback } from "react";
import { callBackendApiPostFormData } from "../../lib/api";
import { getAnswerById } from "../../lib/helpers";
import ErrorPopup from "./ErrorPopup";
import logDrtEvent from "../../lib/logging";

const PhotoUploadQuestion = ({
  onSubmit,
  onNext,
  initialValue,
  answers,
  questions,
  ensureFullyVisible,
}) => {
  const [photos, setPhotos] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const fileInputRef = useRef(null);
  const minPhotos = 3;

  const effectiveCaseID = (answers && questions && getAnswerById(answers, questions, "case-id")) || null;
  const effectivePatientID = (answers && questions && getAnswerById(answers, questions, "patient-id")) || null;

  const openError = useCallback((msg) => {
    setErrorMessage(msg);
    setErrorOpen(true);
  }, []);
  const closeError = useCallback(() => setErrorOpen(false), []);

  // Set initial value when editing
  useEffect(() => {
    if (initialValue && Array.isArray(initialValue)) {
      const normalized = initialValue.map((item, i) => {
        const url = typeof item === "string" ? item : item?.url || "";
        return { file: null, preview: url, isRemote: true, name: `photo-${i + 1}` };
      });
      setPhotos(normalized);
    }
  }, [initialValue]);

  // Cleanup URLs on unmount
  useEffect(() => {
    return () => {
      photos.forEach((p) => {
        if (p?.file && p?.preview) URL.revokeObjectURL(p.preview);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to show Continue button when photos are added/removed
  useEffect(() => {
    if (photos.length > 0 && ensureFullyVisible) {
      // Small delay to allow DOM to update with new photo thumbnails
      const timer = setTimeout(() => {
        ensureFullyVisible();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [photos.length, ensureFullyVisible]);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    if (imageFiles.length === 0) return;

    const newItems = imageFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      isRemote: false,
      name: file.name,
      size: file.size,
      type: file.type,
    }));

    setPhotos((prev) => [...prev, ...newItems]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePhoto = (index) => {
    setPhotos((prev) => {
      const removed = prev[index];
      if (removed?.file && removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const canSubmit =
    photos.filter((p) => !p.isRemote).length + photos.filter((p) => p.isRemote).length >=
    minPhotos;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!effectiveCaseID || !effectivePatientID) {
      openError(
        "If this keeps happening, you can restart your intake process by clicking the Start Over button or text/call us at 781-650-4492."
      );
      return;
    }

    const localFiles = photos.filter((p) => !p.isRemote && p.file).map((p) => p.file);

    if (localFiles.length === 0) {
      const existingUrls = photos.filter((p) => p.isRemote).map((p) => p.preview);
      onSubmit(existingUrls);
      onNext();
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("caseID", effectiveCaseID);
      formData.append("patientID", effectivePatientID);
      localFiles.forEach((file) => formData.append("photos", file));

      const response = await callBackendApiPostFormData(
        "/api/v1/upload-images",
        formData
      );

      if (!response.ok) {
        throw new Error(`Image upload failed with status ${response.status}`);
      }

      const data = await response.json();
      const uploadedUrls = Array.isArray(data?.pre_signed_urls)
        ? data.pre_signed_urls
        : [];

      const existingUrls = photos.filter((p) => p.isRemote).map((p) => p.preview);
      const allUrls = [...existingUrls, ...uploadedUrls];

      onSubmit(allUrls);
      onNext();

      photos.forEach((p) => {
        if (p?.file && p?.preview) URL.revokeObjectURL(p.preview);
      });
      setPhotos([]);
    } catch (err) {
      logDrtEvent(`[conv] Image upload call failed for patient with patientID: ${effectivePatientID} and caseID: ${effectiveCaseID} with err: ${err}`);
      openError(`We couldn't upload your photos. Please try again.\n${String(err)}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="input-form">
        <div className="photo-upload-container">
          <div className="photo-upload-instructions">
            <p>Please upload at least {minPhotos} photos of your skin issue from different angles.</p>
          </div>

          <div className="photo-upload-area">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />

          {/* Visible button that triggers the hidden input */}
          <button
            type="button"
            className="photo-upload-button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? "Uploading..." : "Add Photos"}
          </button>
        </div>

          {photos.length > 0 && (
            <div className="photo-preview-container">
              <h4>
                Uploaded Photos ({photos.length}/{minPhotos} minimum)
              </h4>
              <div className="photo-grid">
                {photos.map((photo, index) => (
                  <div key={index} className="photo-preview-item">
                    <img
                      src={photo.preview}
                      alt={`Upload ${index + 1}`}
                      className="photo-preview"
                    />
                    <button
                      type="button"
                      className="photo-remove-button"
                      onClick={() => removePhoto(index)}
                      disabled={isUploading}
                      aria-label={`Remove photo ${index + 1}`}
                    >
                      x
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            type="submit"
            className="photo-submit-button"
            disabled={(photos.length > 0 && photos.length < minPhotos) || isUploading}
          >
            {isUploading
              ? "Uploading..."
              : photos.length === 0
                ? "Add later"
                : canSubmit
                  ? "Continue"
                  : `Upload ${minPhotos - photos.length} more photos`}
          </button>
        </div>
      </form>

      {/* Error Popup */}
      <ErrorPopup open={errorOpen} message={errorMessage} onClose={closeError} />
    </>
  );
};

export default PhotoUploadQuestion;
